import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';

interface Company {
  id: string;
  name: string;
  contact_email: string;
  contact_phone?: string;
  company_code?: string;
  min_inspection_minutes?: number;
}

interface Subscription {
  vehicle_count: number;
  weekly_cost: number;
  price_per_vehicle: number;
  subscription_status: string;
  next_payment_date?: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    min_inspection_minutes: 0
  });
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [newVehicleCount, setNewVehicleCount] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchCompany();
    fetchSubscription();
  }, []);

  const fetchCompany = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(response.data.data);
      setFormData({
        name: response.data.data.name,
        contact_email: response.data.data.contact_email,
        contact_phone: response.data.data.contact_phone || '',
        min_inspection_minutes: response.data.data.min_inspection_minutes || 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching company:', error);
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data.data;
      // Convert string numbers to actual numbers
      setSubscription({
        ...data,
        vehicle_count: parseInt(data.vehicle_count),
        weekly_cost: parseFloat(data.weekly_cost),
        price_per_vehicle: parseFloat(data.price_per_vehicle)
      });
      setNewVehicleCount(parseInt(data.vehicle_count));
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Don't crash if subscription endpoint fails
      setSubscription(null);
    }
  };

  const handleUpgrade = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/subscription/update-vehicle-count`, 
        { new_vehicle_count: newVehicleCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Subscription updated successfully!');
      setShowUpgradeModal(false);
      fetchSubscription();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update subscription');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/companies`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditing(false);
      fetchCompany();
      alert('Company details updated successfully');
    } catch (error) {
      console.error('Error updating company:', error);
      alert('Failed to update company details');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      console.log('Sending password change request:', {
        currentPassword: passwordData.currentPassword ? '***' : 'empty',
        newPassword: passwordData.newPassword ? '***' : 'empty'
      });
      
      const response = await axios.post(`${API_BASE}/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Password change response:', response.data);
      
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      alert('Password changed successfully');
    } catch (error: any) {
      console.error('Error changing password:', error);
      console.error('Error response:', error.response?.data);
      alert(error.response?.data?.error || error.response?.data?.message || 'Failed to change password');
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!company) {
    return <div className="p-6">Error loading company data. Please try refreshing the page.</div>;
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Company Information</h2>
          {!editing && user?.role === 'admin' && (
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Company Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Contact Email</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Contact Phone</label>
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Minimum Inspection Time (minutes)</label>
              <input
                type="number"
                min={0}
                max={1440}
                value={formData.min_inspection_minutes}
                onChange={(e) => setFormData({ ...formData, min_inspection_minutes: parseInt(e.target.value || '0', 10) })}
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">Set to 0 to disable. If a driver completes faster than this, it will be marked as incomplete.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    name: company?.name || '',
                    contact_email: company?.contact_email || '',
                    contact_phone: company?.contact_phone || '',
                    min_inspection_minutes: company?.min_inspection_minutes || 0
                  });
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-500">Company Code</label>
              <p className="text-2xl font-bold text-blue-600">{company?.company_code}</p>
              <p className="text-xs text-gray-500 mt-1">Share this code with drivers to log in</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Company Name</label>
              <p className="text-lg">{company?.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Contact Email</label>
              <p className="text-lg">{company?.contact_email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Contact Phone</label>
              <p className="text-lg">{company?.contact_phone || 'Not set'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Minimum Inspection Time</label>
              <p className="text-lg">{company?.min_inspection_minutes ? `${company.min_inspection_minutes} minutes` : 'Not set'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Password Change Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Password Settings</h2>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Change Password
        </button>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Change Password</h3>
            <form onSubmit={handlePasswordChange}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  required
                  minLength={6}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Section */}
      {subscription && (user?.role === 'manager' || user?.role === 'admin') && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Subscription</h2>
          <div className="space-y-3 mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Vehicle Limit:</span>
              <span className="font-semibold">{subscription.vehicle_count} vehicles</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Price per Vehicle:</span>
              <span className="font-semibold">£{subscription.price_per_vehicle.toFixed(2)}/week</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Weekly Cost:</span>
              <span className="font-semibold text-lg">£{subscription.weekly_cost.toFixed(2)}/week</span>
            </div>
          </div>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Upgrade Subscription
          </button>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Upgrade Subscription</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">New Vehicle Count</label>
              <input
                type="number"
                min={subscription?.vehicle_count || 1}
                value={newVehicleCount}
                onChange={(e) => setNewVehicleCount(parseInt(e.target.value))}
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-sm text-gray-500 mt-1">
                Current: {subscription?.vehicle_count} vehicles
              </p>
            </div>
            {newVehicleCount > (subscription?.vehicle_count || 0) && (
              <div className="bg-blue-50 p-3 rounded mb-4">
                <p className="text-sm">
                  New weekly cost: <strong>£{(newVehicleCount * (newVehicleCount >= 100 ? 0.30 : newVehicleCount >= 50 ? 0.40 : 0.50)).toFixed(2)}/week</strong>
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpgrade}
                disabled={newVehicleCount <= (subscription?.vehicle_count || 0)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                Confirm Upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">User Information</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-500">Name</label>
            <p className="text-lg">{user?.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Email</label>
            <p className="text-lg">{user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Role</label>
            <p className="text-lg capitalize">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
